<template>
  <div class="sort-header">
    <div class="flex justify-between flex-grow">
      <slot></slot>
      <v-popover
        v-if="!isMobile"
        interactive
        :trigger="'click'"
        :role="'poper'"
      >
        <div
          class="sort-header-options-wrap usn"
          :class="{ 'ml-auto': !$slots.default }"
        >
          <div
            v-if="displayViewModeSwitch && hasResources"
            class="sort-header-item pt"
            :class="{ 'sort-header-item-selected': displayViewDropdown }"
          >
            <em
              :style="currentListViewIconStyle"
              class="custom-icons"
            />
            <span class="selected-list-view"> {{ currentListViewContent }} </span>
            <em
              :style="arrowButtonIconStyle"
              class="custom-icons"
            />
          </div>
        </div>
        <template #content="{ state, hide }">
          <ListDropdown
            v-if="state.isVisible"
            ref="dropdown"
            class="sort-header-options-dropdown"
            :width="211"
            :items="settingsViewItems"
            @click="handleViewChange($event, hide)"
            @closeEscape="hide"
          />
        </template>
      </v-popover>
    </div>
  </div>
</template>

<script>
import ListDropdown from '@/components/widgets/ListDropdown';
import useCustomIcon from '@/utilities/composables/useCustomIcon';

const viewTypes = {
  list: 'list',
  grid: 'grid',
};

export default {
  name: 'SortHeader',
  components: {
    ListDropdown,
  },
  props: {
    resourceType: {
      type: String,
      required: false,
      default: 'pages',
    },
    hasResources: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup() {
    const { getCustomIconStyle, iconPath } = useCustomIcon();

    return { getCustomIconStyle, iconPath };
  },
  data() {
    return {
      displayViewDropdown: false,
    };
  },

  computed: {
    pageListView() {
      return this.$store.getters.getCurrentProjectPageColumnLayout;
    },

    boardListView() {
      return this.$store.getters.getCurrentProjectBoardColumnLayout;
    },

    isPageListView() {
      return this.resourceType === 'pages';
    },

    listViewName() {
      return this.isPageListView ? 'pageListView' : 'boardListView';
    },

    listView() {
      return this.isPageListView ? this.pageListView : this.boardListView;
    },

    isListViewTypeList() {
      return Object.is(this.listView, viewTypes.list);
    },

    settingsViewItems() {
      return [
        {
          id: viewTypes.list,
          label: this.translate('oneColumn'),
          customIconName: this.iconPath(viewTypes.list),
          radioButton: true,
          radioName: this.listViewName,
          selected: this.listView,
        },
        {
          id: viewTypes.grid,
          label: this.translate('twoColumns'),
          customIconName: this.iconPath(viewTypes.grid),
          radioButton: true,
          radioName: this.listViewName,
          selected: this.listView,
        },
      ];
    },

    displayFilterSwitch() {
      return !['bookmarked'].includes(this.$route.name);
    },

    displayViewModeSwitch() {
      return !['bookmarked', 'archived'].includes(this.$route.name);
    },

    currentListViewIconStyle() {
      // listView is a string of either 'list' or 'grid'
      return this.getCustomIconStyle(this.iconPath(this.listView));
    },

    arrowButtonIconStyle() {
      return this.getCustomIconStyle(
        this.iconPath(this.displayViewDropdown ? 'chevron-up' : 'chevron-down')
      );
    },

    currentListViewContent() {
      return this.translate(this.isListViewTypeList ? 'oneColumn' : 'twoColumns');
    },
  },

  mounted() {
    window.addEventListener('popstate', this.handlePopstate);
  },

  unmounted() {
    window.removeEventListener('popstate', this.handlePopstate);
  },

  methods: {
    handleViewDropdown() {
      this.displayViewDropdown = !this.displayViewDropdown;
    },
    hideViewDropdown() {
      this.displayViewDropdown = false;
    },
    handleViewChange(item, hideFn) {
      if (typeof hideFn === 'function') {
        hideFn();
      }
      this.isPageListView ? this.setPageListView(item) : this.setBoardListView(item);
    },
    setPageListView(item) {
      this.$store.dispatch('setPagesColumnLayout', { layout: item.id });
    },
    setBoardListView(item) {
      this.$store.dispatch('toggleProjectBoards', { value: false });
      this.$store.dispatch('setBoardsColumnLayout', { layout: item.id });
    },
    handlePopstate() {
      this.displayViewDropdown = false;
    },
  },
};
</script>

<style lang="scss">
.sort-header-options-dropdown {
  position: relative;
  @apply shadow-md;
}
.sort-header {
  font-family: $primaryFontFamily;
  display: flex;
  align-items: center;
  margin: 16px 0px 8px;
  padding: $spaceHomePagePadding;

  .sort-header-options-wrap {
    @apply flex;

    .sort-header-item {
      @apply flex items-center py-1 px-2 gap-2 rounded;

      .custom-icons {
        @apply bg-icon-default shrink-0 h-4 w-4;
      }

      &:hover,
      &.sort-header-item-selected {
        @apply bg-hover-default;
        .custom-icons {
          @apply bg-icon-hover;
        }
      }

      .selected-list-view {
        @apply text-sm text-text-default shrink-0;
      }
    }
  }
}
</style>
